"use strict";
/**
 * One-pass readings of markup and CSS that the import used to take with
 * regular expressions which retry from every start.
 *
 * Each function here returns EXACTLY what the expression named in its comment
 * returns, on every input, and costs time in proportion to the input. The old
 * expressions were right on real email and wrong on a crafted one: a run of
 * comments before a missing doctype backtracked exponentially (28 comments,
 * 378 characters, stalled a conversion for two minutes), and a tail of
 * unclosed `<style`, `<link`, `<font` or `<img` tags, or a stylesheet with no
 * brace in it, made every start scan to the end of the document, which is the
 * square of its length: seconds at 100 KB, a timeout at 400 KB, all of it
 * inside one synchronous parse that stalls every request in the process.
 *
 * The tests keep each old expression as an oracle and check both agree on
 * seeded random input and on every template of the corpus.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.spaceAfterFirstImgDropped = exports.fontFamilyDecls = exports.endsInClassOrId = exports.someStyleNames = exports.unlayerIdRules = exports.readStyleElements = exports.withoutImportant = exports.withoutImportantTail = exports.importantTailAt = exports.backgroundUrlArg = exports.maskTemplateTags = exports.quotedUrlMatches = exports.withoutEntityQuotes = exports.quoteLengthBefore = exports.quoteKind = exports.quoteLengthAt = exports.cssUrlAt = exports.cssUrlMatches = exports.cssUrlArgs = exports.dropCssComments = exports.relNamesStylesheet = exports.replaceComments = exports.cssRuleSpans = exports.rawElementStrings = exports.msoConditionals = exports.nearClosedAnchors = exports.pairedElements = exports.rawElements = exports.paintedRefs = exports.openTagsReplaced = exports.fontColorTagsReplaced = exports.TagAttrScan = exports.lastQuotedAttrs = exports.tagsHolding = exports.someTagHolds = exports.spaceTag = exports.noTag = exports.TAGS_NONEMPTY = exports.TAGS = exports.replaceTags = exports.firstHolding = exports.replaceThroughLast = exports.throughLast = exports.opensAsSvg = exports.opensWithDoctype = exports.replaceSpans = exports.NextOf = exports.skipSpace = exports.clipText = exports.cutText = void 0;
exports.tableGapsThroughMarkup = exports.tableGapsThroughComments = exports.liquidQuotesInAttributes = exports.liquidTagSpans = exports.liquidStringsQuoted = exports.liquidTagEnds = exports.tableGaps = exports.spaceBeforeFirstImgDropped = void 0;
const at = (re, s, i) => {
    re.lastIndex = i;
    return re.test(s);
};
/**
 * The first `max` UTF-16 units of `text`, one fewer when the cut would fall
 * inside a surrogate pair (an emoji, any character past U+FFFF): a cut on
 * code points, never leaving half a character. `text.slice(0, max)` left
 * the pair's first half at the end; a design holding it is refused by the
 * database (a lone surrogate in jsonb), and a browser draws it as U+FFFD. A
 * preheader of "a" and 700 emoji, cut at 1,000, made the whole import fail.
 */
function cutText(text, max) {
    if (text.length <= max)
        return text;
    let end = Math.max(0, Math.floor(max));
    const last = text.charCodeAt(end - 1);
    if (end > 0 && last >= 0xd800 && last <= 0xdbff)
        end -= 1;
    return text.slice(0, end);
}
exports.cutText = cutText;
/** `text` when it is at most `max` long, else cut on code points (cutText) with `mark` after it, `max` in all. */
function clipText(text, max, mark = "...") {
    return text.length > max ? `${cutText(text, max - mark.length)}${mark}` : text;
}
exports.clipText = clipText;
const SPACE = /\s*/y;
/** The index after the run of `\s` characters that starts at `i` (`i` when there is none). */
function skipSpace(s, i) {
    SPACE.lastIndex = i;
    SPACE.exec(s);
    return SPACE.lastIndex;
}
exports.skipSpace = skipSpace;
/**
 * The first index at or after `from` of any of `chars`, or -1. The string
 * is read once, forward, as far as the questions need, and every place of
 * those characters found is kept: asked from many starts, in any order, it
 * never reads a long run of none of them twice, where a fresh search from
 * every start does.
 */
class NextOf {
    constructor(s, chars) {
        this.s = s;
        this.hits = [];
        this.scanned = 0;
        this.others = new Map();
        this.re = new RegExp(`[${chars.replace(/[\\\]^-]/g, "\\$&")}]`, "g");
    }
    /** The finder of other characters in the same string, made once (see urlArgAt: a quoted address's own quote). */
    of(chars) {
        let other = this.others.get(chars);
        if (!other) {
            other = new NextOf(this.s, chars);
            this.others.set(chars, other);
        }
        return other;
    }
    from(i) {
        const h = this.hits;
        if (h.length && h[h.length - 1] >= i) {
            let lo = 0;
            let hi = h.length - 1;
            while (lo < hi) {
                const mid = (lo + hi) >> 1;
                if (h[mid] < i)
                    lo = mid + 1;
                else
                    hi = mid;
            }
            return h[lo];
        }
        while (this.scanned <= this.s.length) {
            this.re.lastIndex = this.scanned;
            const m = this.re.exec(this.s);
            if (!m) {
                this.scanned = this.s.length + 1;
                break;
            }
            h.push(m.index);
            this.scanned = m.index + 1;
            if (m.index >= i)
                return m.index;
        }
        return -1;
    }
}
exports.NextOf = NextOf;
/** `s` with each of `spans` (in order, not overlapping) replaced by what `to` gives for it. */
function replaceSpans(s, spans, to) {
    if (!spans.length)
        return s;
    let out = "";
    let last = 0;
    for (const span of spans) {
        out += s.slice(last, span.start) + to(span);
        last = span.end;
    }
    return out + s.slice(last);
}
exports.replaceSpans = replaceSpans;
// ---------------------------------------------------------------------------
// What a document or a drawing opens with
const DOCTYPE = /<!doctype\b/iy;
/**
 * `/^\s*(?:<!--[\s\S]*?-->\s*)*<!doctype\b/i.test(html)`.
 *
 * The comments before the doctype are one `<!--`, then anything, then a
 * `-->` that only space parts from the doctype: the lazy body may run past a
 * `-->` into the next comment, so any number of comments reads as one.
 */
function opensWithDoctype(html) {
    const i = skipSpace(html, 0);
    if (at(DOCTYPE, html, i))
        return true;
    if (!html.startsWith("<!--", i))
        return false;
    for (let q = html.indexOf("-->", i + 4); q >= 0; q = html.indexOf("-->", q + 3))
        if (at(DOCTYPE, html, skipSpace(html, q + 3)))
            return true;
    return false;
}
exports.opensWithDoctype = opensWithDoctype;
const SVG_OPEN = /<svg[\s>]/iy;
const XML_DECL = /<\?xml/iy;
const SVG_DOCTYPE = /<!DOCTYPE/iy;
/**
 * `/^\s*(?:\ufeff)?\s*(?:<\?xml[^>]*>\s*)?(?:<!--[\s\S]*?-->\s*|<!DOCTYPE[^>]*>\s*)*<svg[\s>]/i.test(text)`.
 *
 * Read forward over the places an `<svg` may start: after the leading space,
 * after an XML declaration, after a doctype, and, once a comment has opened
 * at one of them, after every later `-->` (the lazy body may run through
 * several). Every place is visited once.
 */
function opensAsSvg(text) {
    const n = text.length;
    const p0 = skipSpace(text, 0);
    if (at(SVG_OPEN, text, p0))
        return true;
    const mark = new Uint8Array(n + 1);
    let furthest = p0;
    const reach = (j) => {
        mark[j] = 1;
        if (j > furthest)
            furthest = j;
    };
    const gt = new NextOf(text, ">");
    mark[p0] = 1;
    if (at(XML_DECL, text, p0)) {
        const g = gt.from(p0 + 5);
        if (g >= 0)
            reach(skipSpace(text, g + 1));
    }
    let commentFrom = Infinity;
    for (let i = p0; i <= n; i += 1) {
        if (i > furthest && commentFrom === Infinity)
            return false;
        if (i >= commentFrom && text.startsWith("-->", i))
            reach(skipSpace(text, i + 3));
        if (!mark[i])
            continue;
        if (at(SVG_OPEN, text, i))
            return true;
        if (text.startsWith("<!--", i)) {
            if (i + 4 < commentFrom)
                commentFrom = i + 4;
        }
        else if (at(SVG_DOCTYPE, text, i)) {
            const g = gt.from(i + 9);
            if (g >= 0)
                reach(skipSpace(text, g + 1));
        }
    }
    return false;
}
exports.opensAsSvg = opensAsSvg;
// ---------------------------------------------------------------------------
// Tags
/**
 * `s` up to and including its last `ch` (empty when it has none). A pattern
 * that must end at a `ch` it reaches through `[^ch]*` (`<img\b[^>]*>`,
 * `@media([^{]*)\{`), global or asked for its first match, finds the same
 * matches in it as in `s`, since none can end past that last `ch`, and in it
 * every start reaches a `ch` instead of scanning to the end and failing.
 */
function throughLast(s, ch = ">") {
    return s.slice(0, s.lastIndexOf(ch) + 1);
}
exports.throughLast = throughLast;
/**
 * `s.replace(re, rep)` for a global `re` whose every match ends at a `ch` it
 * reaches through `[^ch]*` (see throughLast): the part after the last `ch`
 * holds no match and is kept as it is.
 */
function replaceThroughLast(s, re, rep, ch = ">") {
    const cut = s.lastIndexOf(ch) + 1;
    return s.slice(0, cut).replace(re, rep) + s.slice(cut);
}
exports.replaceThroughLast = replaceThroughLast;
/**
 * The index where `/OPEN[^STOPS]*INNER/` (or, with `needStop`,
 * `/OPEN[^STOPS]*INNER[^STOPS]*[STOP]/`) first matches in `s`, or -1: the
 * first match of `open` (a global regex) whose run, from the end of that
 * match up to the next of `stops` (or the end of `s`), holds `inner`. INNER
 * can hold none of `stops`, and must not be able to match at the character
 * before the run, which is kept in front of it so a leading `\b` reads the
 * same. Starts inside one run share its end and read a part of it, so the
 * scan moves on past that end.
 */
function firstHolding(s, open, stops, inner, needStop = false, lead = 1, peek = false) {
    const next = new NextOf(s, stops);
    let from = 0;
    for (;;) {
        open.lastIndex = from;
        const m = open.exec(s);
        if (!m)
            return -1;
        const runStart = m.index + m[0].length;
        const stop = next.from(runStart);
        if (stop < 0 && needStop)
            return -1;
        const end = stop < 0 ? s.length : stop;
        inner.lastIndex = 0;
        // (`lead` characters before the run are kept for a leading `\b`; with
        // `peek` the stop itself is kept for a lookahead at the run's end)
        if (inner.test(s.slice(runStart - lead, peek && stop >= 0 ? end + 1 : end)))
            return m.index;
        if (stop < 0)
            return -1;
        from = stop + 1;
    }
}
exports.firstHolding = firstHolding;
/**
 * `s.replace(/<[^>]*>/g, rep)`, or with `plus` `s.replace(/<[^>]+>/g, rep)`,
 * for a plain replacement string (no `$` patterns).
 */
function replaceTags(s, rep = "", plus = false) {
    let lt = s.indexOf("<");
    if (lt < 0)
        return s;
    let out = "";
    let from = 0;
    while (lt >= 0) {
        const gt = s.indexOf(">", lt + 1);
        // no `>` after this `<`: none after any later one either
        if (gt < 0)
            break;
        if (plus && gt === lt + 1) {
            lt = s.indexOf("<", lt + 1);
            continue;
        }
        out += s.slice(from, lt) + rep;
        from = gt + 1;
        lt = s.indexOf("<", from);
    }
    return out + s.slice(from);
}
exports.replaceTags = replaceTags;
/**
 * `s.replace(TAGS, noTag)` is `s.replace(/<[^>]*>/g, "")`, and with
 * TAGS_NONEMPTY `/<[^>]+>/g`; with spaceTag, each tag becomes " ". A `<`
 * with no `>` after it matches up to the end here, once, and is given back
 * unchanged, where the plain pattern retried from every such `<` to the end.
 */
exports.TAGS = /<[^>]*(?:>|$)/g;
exports.TAGS_NONEMPTY = /<[^>]+(?:>|$)/g;
const noTag = (m) => (m.endsWith(">") ? "" : m);
exports.noTag = noTag;
const spaceTag = (m) => (m.endsWith(">") ? " " : m);
exports.spaceTag = spaceTag;
/**
 * `/<font\b[^>]*INNER/i.test(s)` where `open` is `/<font\b/gi` and INNER can
 * hold no `>`: whether a tag holds `inner` before its own first `>` (see
 * firstHolding).
 */
function someTagHolds(s, open, inner) {
    return firstHolding(s, open, ">", inner) >= 0;
}
exports.someTagHolds = someTagHolds;
/**
 * The matches of `/<link\b[^>]*INNER[^>]*>/gi` where `open` is `/<link\b/gi`
 * and INNER can hold no `>`: the tags, each from its first start to its own
 * first `>`, whose text after the name holds `inner` (see firstHolding for
 * `lead`). Starts inside one tag share its `>`, so each tag is read once.
 */
function tagsHolding(s, open, inner, lead = 1) {
    const out = [];
    let from = 0;
    for (;;) {
        open.lastIndex = from;
        const m = open.exec(s);
        if (!m)
            break;
        const nameEnd = m.index + m[0].length;
        const gt = s.indexOf(">", nameEnd);
        if (gt < 0)
            break;
        inner.lastIndex = 0;
        if (inner.test(s.slice(nameEnd - lead, gt)))
            out.push({ start: m.index, end: gt + 1 });
        from = gt + 1;
    }
    return out;
}
exports.tagsHolding = tagsHolding;
/**
 * The matches of `/<font\b[^>]*\sface\s*=\s*(?:"([^"]*)"|'([^']*)')/gi`,
 * given `open` (`/<font\b/gi`) and `attr`, a global regex for the part from
 * the space through the opening quote (`/\sface\s*=\s*(["'])/gi`, the quote
 * its first group): where each match starts and ends, the value and its
 * quote. The greedy `[^>]*` makes a match take the LAST such attribute
 * before the tag's first `>` whose value closes; the value itself may run
 * past that `>`.
 */
function lastQuotedAttrs(s, open, attr) {
    const out = [];
    const scan = new TagAttrScan(s, open, attr);
    for (let m = scan.firstFrom(0); m; m = scan.firstFrom(m.end))
        out.push(m);
    return out;
}
exports.lastQuotedAttrs = lastQuotedAttrs;
/**
 * The first match at or after a place of the pattern lastQuotedAttrs reads,
 * remembered while it lies ahead of the places asked for.
 */
class TagAttrScan {
    constructor(s, open, attr) {
        this.s = s;
        this.open = open;
        this.cands = [];
        this.closes = [];
        this.lastFrom = -1;
        attr.lastIndex = 0;
        for (let m = attr.exec(s); m; m = attr.exec(s)) {
            this.cands.push({ at: m.index, valueStart: m.index + m[0].length, quote: m[1] });
            attr.lastIndex = m.index + 1;
        }
        this.dq = new NextOf(s, '"');
        this.sq = new NextOf(s, "'");
        this.gts = new NextOf(s, ">");
    }
    closeOf(k) {
        if (this.closes[k] === undefined)
            this.closes[k] = (this.cands[k].quote === '"' ? this.dq : this.sq).from(this.cands[k].valueStart);
        return this.closes[k];
    }
    /** the index of the first candidate at or after `i` */
    candFrom(i) {
        let lo = 0;
        let hi = this.cands.length;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (this.cands[mid].at < i)
                lo = mid + 1;
            else
                hi = mid;
        }
        return lo;
    }
    firstFrom(pos) {
        if (this.last !== undefined && pos >= this.lastFrom && (this.last === null || this.last.start >= pos))
            return this.last;
        this.lastFrom = pos;
        const { s, open } = this;
        let from = pos;
        for (;;) {
            open.lastIndex = from;
            const o = open.exec(s);
            if (!o)
                return (this.last = null);
            const nameEnd = o.index + o[0].length;
            const gt = this.gts.from(nameEnd);
            const end = gt < 0 ? s.length : gt;
            // the last candidate before the `>` whose value closes
            for (let k = this.candFrom(end) - 1; k >= 0 && this.cands[k].at >= nameEnd; k -= 1) {
                const close = this.closeOf(k);
                if (close < 0)
                    continue;
                const c = this.cands[k];
                return (this.last = { start: o.index, end: close + 1, value: s.slice(c.valueStart, close), quote: c.quote });
            }
            if (gt < 0)
                return (this.last = null);
            from = gt + 1;
        }
    }
}
exports.TagAttrScan = TagAttrScan;
/**
 * `s.replace(/<font\b[^>]*?\bcolor\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))[^>]*>/gi, (_m, _q, a, b, c) => rep(a ?? b ?? c))`,
 * read in one pass. The pattern retried from every `<font` of a tag with no
 * `>` after it to the end of the text, so text of many unclosed `<font` took
 * time in the square of its length (edit_email's text path: 1.6 s at 100 KB).
 *
 * At a `<font`, the first `color=` (a whole word, any case) before the tag's
 * first `>` whose value is followed by a `>` wins: a value in double or
 * single quotes runs to its closing quote, past a `>` if it holds one, and
 * the tag ends at the first `>` after it; otherwise (no closing quote, or no
 * `>` after it) the value is the run of characters up to a space or a `>`,
 * and the tag ends at the first `>` after it. A tag none of whose values is
 * followed by a `>` is left as it is, and so is every `<font` inside it (they
 * see the same values), so the scan moves on past its `>`.
 */
function fontColorTagsReplaced(s, rep) {
    const open = /<font\b/gi;
    // where `\bcolor\s*=\s*` matches, and where each one's value starts
    const cands = [];
    const color = /\bcolor\s*=\s*/gi;
    for (let m = color.exec(s); m; m = color.exec(s))
        cands.push({ at: m.index, value: m.index + m[0].length });
    const gts = new NextOf(s, ">");
    const quotes = { '"': new NextOf(s, '"'), "'": new NextOf(s, "'") };
    const run = /[^\s>]+/y;
    let out = "";
    let copied = 0;
    let from = 0;
    let k = 0;
    for (;;) {
        open.lastIndex = from;
        const o = open.exec(s);
        if (!o)
            break;
        const nameEnd = o.index + o[0].length;
        const gt = gts.from(nameEnd);
        // no `>` after this `<font`: none after any value of it or of a later one
        if (gt < 0)
            break;
        while (k < cands.length && cands[k].at < nameEnd)
            k += 1;
        let end = -1;
        let value = "";
        for (let j = k; end < 0 && j < cands.length && cands[j].at < gt; j += 1) {
            const v = cands[j].value;
            const q = s[v];
            if (q === '"' || q === "'") {
                const close = quotes[q].from(v + 1);
                const after = close < 0 ? -1 : gts.from(close + 1);
                if (after >= 0) {
                    value = s.slice(v + 1, close);
                    end = after + 1;
                    continue;
                }
            }
            run.lastIndex = v;
            const r = v < s.length ? run.exec(s) : null;
            const after = r ? gts.from(v) : -1;
            if (r && after >= 0) {
                value = r[0];
                end = after + 1;
            }
        }
        if (end < 0) {
            from = gt + 1;
            continue;
        }
        out += s.slice(copied, o.index) + rep(value);
        copied = end;
        from = end;
    }
    return out + s.slice(copied);
}
exports.fontColorTagsReplaced = fontColorTagsReplaced;
/**
 * `s.replace(/OPEN[^>]*>/gi, rep)` for `open` the global regex /OPEN/gi (such
 * as `/<font\b/gi`) and a plain replacement string, read in one pass: a tag
 * runs from its start to the first `>` after its name, and once a start has
 * no `>` after it no later one has either (the pattern retried from each such
 * start to the end of the text).
 */
function openTagsReplaced(s, open, rep) {
    let out = "";
    let copied = 0;
    for (;;) {
        open.lastIndex = copied;
        const o = open.exec(s);
        if (!o)
            break;
        const gt = s.indexOf(">", o.index + o[0].length);
        if (gt < 0)
            break;
        out += s.slice(copied, o.index) + rep;
        copied = gt + 1;
    }
    return out + s.slice(copied);
}
exports.openTagsReplaced = openTagsReplaced;
/**
 * The matches of
 * `/\sbackground\s*=\s*("([^"]*)"|'([^']*)')|url\(\s*(['"]?)([^'")]+)\4\s*\)|<v:(?:fill|image)\b[^>]*\ssrc\s*=\s*("([^"]*)"|'([^']*)')/gi`,
 * in order, as the address each names. The three alternatives start with
 * different characters, so the match at a place is the one that starts
 * there; each is found by a reading of its own from the place the last
 * match ended.
 */
function paintedRefs(s) {
    const out = [];
    const attrRe = /\sbackground\s*=\s*("([^"]*)"|'([^']*)')/gi;
    let attr;
    const attrFrom = (pos) => {
        var _a;
        if (attr === undefined || (attr && attr.start < pos)) {
            attrRe.lastIndex = pos;
            const m = attrRe.exec(s);
            attr = m ? { start: m.index, end: m.index + m[0].length, ref: (_a = m[2]) !== null && _a !== void 0 ? _a : m[3] } : null;
        }
        return attr;
    };
    const stops = new NextOf(s, `'")`);
    let url;
    const urlFrom = (pos) => {
        if (url === undefined || (url && url.start < pos)) {
            url = null;
            for (let from = pos;;) {
                URL_WORD.lastIndex = from;
                const m = URL_WORD.exec(s);
                if (!m)
                    break;
                const hit = urlArgAt(s, m.index, stops, false, true);
                if (hit) {
                    url = { start: m.index, end: hit.end, ref: hit.arg };
                    break;
                }
                from = m.index + 1;
            }
        }
        return url;
    };
    const vml = new TagAttrScan(s, /<v:(?:fill|image)\b/gi, /\ssrc\s*=\s*(["'])/gi);
    for (let pos = 0;;) {
        const a = attrFrom(pos);
        const u = urlFrom(pos);
        const v = vml.firstFrom(pos);
        const first = [a, u, v && { start: v.start, end: v.end, ref: v.value }]
            .filter((x) => Boolean(x))
            .sort((x, y) => x.start - y.start)[0];
        if (!first)
            break;
        out.push(first.ref);
        pos = first.end;
    }
    return out;
}
exports.paintedRefs = paintedRefs;
// ---------------------------------------------------------------------------
// Raw text elements (style, script)
const openOf = new Map();
const closeOf = new Map();
/**
 * The matches of `/<NAME\b([^>]*)>([\s\S]*?)<\/NAME>/gi` (with `closeSpace`,
 * `<\/NAME\s*>`), in order: where each starts, the index of the `>` that ends
 * its start tag, where its closing tag starts, and where it ends.
 *
 * Once a start finds no `>` after it, or no closing tag after its `>`, no
 * later start can find one either, so the scan stops there.
 */
function rawElements(s, name, closeSpace = false) {
    const out = [];
    const key = name.toLowerCase();
    let open = openOf.get(key);
    if (!open)
        openOf.set(key, (open = new RegExp(`<${key}\\b`, "gi")));
    const ck = `${key}|${closeSpace ? 1 : 0}`;
    let close = closeOf.get(ck);
    if (!close)
        closeOf.set(ck, (close = new RegExp(closeSpace ? `<\\/${key}\\s*>` : `<\\/${key}>`, "gi")));
    let from = 0;
    for (;;) {
        open.lastIndex = from;
        const o = open.exec(s);
        if (!o)
            break;
        const gt = s.indexOf(">", o.index + o[0].length);
        if (gt < 0)
            break;
        close.lastIndex = gt + 1;
        const c = close.exec(s);
        if (!c)
            break;
        out.push({ start: o.index, gt, close: c.index, end: c.index + c[0].length });
        from = c.index + c[0].length;
    }
    return out;
}
exports.rawElements = rawElements;
/**
 * The matches of `/<(A|B|...)\b([^>]*)>([\s\S]*?)<\/\1>/gi` (or, with
 * `throughGt` false, `/<(A|B|...)\b[\s\S]*?<\/\1>/gi`), where `open` is
 * `/<(A|B|...)\b/gi`: where each starts, the name as written, the index of
 * the `>` that ends its start tag (-1 without `throughGt`), where its closing
 * tag starts, and where it ends. `limit` bounds the first match only (`exec`
 * of the same pattern without `g`).
 *
 * A name whose start finds no closing tag after it finds none after any
 * later start of it, so it is left out from there; and once a start finds
 * no `>` after it, no later start can.
 */
function pairedElements(s, open, throughGt = true, limit = Infinity) {
    const out = [];
    const dead = new Set();
    const closes = new Map();
    let from = 0;
    while (out.length < limit) {
        open.lastIndex = from;
        const o = open.exec(s);
        if (!o)
            break;
        const key = o[1].toLowerCase();
        if (dead.has(key)) {
            from = o.index + 1;
            continue;
        }
        const nameEnd = o.index + o[0].length;
        let gt = -1;
        if (throughGt) {
            gt = s.indexOf(">", nameEnd);
            if (gt < 0)
                break;
        }
        let close = closes.get(key);
        if (!close)
            closes.set(key, (close = new RegExp(`<\\/${key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}>`, "gi")));
        close.lastIndex = throughGt ? gt + 1 : nameEnd;
        const c = close.exec(s);
        if (!c) {
            dead.add(key);
            from = o.index + 1;
            continue;
        }
        out.push({ start: o.index, name: o[1], gt, close: c.index, end: c.index + c[0].length });
        from = c.index + c[0].length;
    }
    return out;
}
exports.pairedElements = pairedElements;
/**
 * `s.match(/<a\b[^>]*>[\s\S]{0,N}?<\/a>/gi) || []`: each anchor whose first
 * `</a>` after its start tag's `>` comes within `limit` characters.
 */
function nearClosedAnchors(s, limit) {
    const out = [];
    const open = /<a\b/gi;
    const close = /<\/a>/gi;
    let c;
    const closeFrom = (i) => {
        if (c === undefined || (c && c.index < i)) {
            close.lastIndex = i;
            c = close.exec(s);
        }
        return c;
    };
    let from = 0;
    for (;;) {
        open.lastIndex = from;
        const o = open.exec(s);
        if (!o)
            break;
        const gt = s.indexOf(">", o.index + 2);
        if (gt < 0)
            break;
        const k = closeFrom(gt + 1);
        if (k && k.index - (gt + 1) <= limit) {
            out.push(s.slice(o.index, k.index + 4));
            from = k.index + 4;
        }
        else
            from = gt + 1;
    }
    return out;
}
exports.nearClosedAnchors = nearClosedAnchors;
/**
 * The matches of `/<!--\[if[^\]]{0,200}\]>([\s\S]{0,20000}?)<!\[endif\]-->/gi`
 * (with the two bounds given): where each starts and ends, and its body. The
 * condition ends at its first `]`, which a `>` must follow; the body ends at
 * the first `<![endif]-->` after it.
 */
function msoConditionals(s, condMax = 200, bodyMax = 20000) {
    const out = [];
    const open = /<!--\[if/gi;
    const close = /<!\[endif\]-->/gi;
    const brackets = new NextOf(s, "]");
    let c;
    const closeFrom = (i) => {
        if (c === undefined || (c && c.index < i)) {
            close.lastIndex = i;
            c = close.exec(s);
        }
        return c;
    };
    let from = 0;
    for (;;) {
        open.lastIndex = from;
        const o = open.exec(s);
        if (!o)
            break;
        from = o.index + 1;
        const r = brackets.from(o.index + 7);
        if (r < 0 || r - (o.index + 7) > condMax || s[r + 1] !== ">")
            continue;
        const k = closeFrom(r + 2);
        if (!k || k.index - (r + 2) > bodyMax)
            continue;
        out.push({ start: o.index, end: k.index + k[0].length, bodyStart: r + 2, bodyEnd: k.index });
        from = k.index + k[0].length;
    }
    return out;
}
exports.msoConditionals = msoConditionals;
/** `s.match(/<NAME\b[^>]*>[\s\S]*?<\/NAME>/gi) || []` */
function rawElementStrings(s, name) {
    return rawElements(s, name).map((e) => s.slice(e.start, e.end));
}
exports.rawElementStrings = rawElementStrings;
// ---------------------------------------------------------------------------
// CSS
/**
 * The matches of `/([^{}]+)\{([^{}]*)\}/g` (with `emptySelector`,
 * `/[^{}]*\{[^{}]*\}/g`), in order: a `{` whose next brace is a `}`, with the
 * selector running back to the brace before it.
 */
function cssRuleSpans(css, emptySelector = false) {
    const out = [];
    let lastBrace = -1;
    let open = -1;
    let beforeOpen = -1;
    const re = /[{}]/g;
    for (let m = re.exec(css); m; m = re.exec(css)) {
        const i = m.index;
        if (m[0] === "{") {
            beforeOpen = lastBrace;
            open = i;
        }
        else {
            if (open >= 0 && open === lastBrace) {
                const start = beforeOpen + 1;
                if (emptySelector || start < open)
                    out.push({ start, open, close: i });
            }
            open = -1;
        }
        lastBrace = i;
    }
    return out;
}
exports.cssRuleSpans = cssRuleSpans;
/**
 * `s.replace(/<!--[\s\S]*?-->/g, rep)` for a plain replacement string: once
 * a comment finds no `-->`, no later one can.
 */
function replaceComments(s, rep) {
    let out = "";
    let from = 0;
    for (;;) {
        const open = s.indexOf("<!--", from);
        if (open < 0)
            break;
        const close = s.indexOf("-->", open + 4);
        if (close < 0)
            break;
        out += s.slice(from, open) + rep;
        from = close + 3;
    }
    return from ? out + s.slice(from) : s;
}
exports.replaceComments = replaceComments;
/**
 * `/\brel\s*=\s*["']?[^"'>]*stylesheet/i.test(tag)`: a `rel=` whose value, up
 * to its next quote or `>`, holds "stylesheet". Every `rel=` in one such run
 * shares its end, so each is checked against the last "stylesheet" in it.
 */
function relNamesStylesheet(tag) {
    const rel = /\brel\s*=\s*["']?/gi;
    const words = [];
    const word = /stylesheet/gi;
    for (let m = word.exec(tag); m; m = word.exec(tag)) {
        words.push(m.index);
        word.lastIndex = m.index + 1;
    }
    if (!words.length)
        return false;
    const stops = new NextOf(tag, `"'>`);
    let k = 0;
    for (let m = rel.exec(tag); m; m = rel.exec(tag)) {
        const v = m.index + m[0].length;
        const e = stops.from(v);
        const end = e < 0 ? tag.length : e;
        while (k < words.length && words[k] < v)
            k += 1;
        if (k < words.length && words[k] + 10 <= end)
            return true;
        rel.lastIndex = m.index + 1;
    }
    return false;
}
exports.relNamesStylesheet = relNamesStylesheet;
/** `css.replace(/\/\*[\s\S]*?\*\//g, "")` */
function dropCssComments(css) {
    let out = "";
    let from = 0;
    for (;;) {
        const open = css.indexOf("/*", from);
        if (open < 0)
            break;
        const close = css.indexOf("*/", open + 2);
        if (close < 0)
            break;
        out += css.slice(from, open);
        from = close + 2;
    }
    return from ? out + css.slice(from) : css;
}
exports.dropCssComments = dropCssComments;
/**
 * The argument of a CSS `url(...)` that starts at `u` (the `u` of `url`), as
 * `url\s*\(\s*(['"]?)([^'")]*)\1\s*\)` reads it there (with `spaceBeforeParen`
 * false, no `\s*` before the paren; with `nonEmpty`, `([^'")]+)`): the
 * argument, where it starts and the index after the match, or undefined
 * when no match starts there. `stops` finds the next quote or `)`. With
 * `otherQuote`, a quoted address may hold the other quote, as CSS reads it
 * (`url("a.png?e={{ person['id'] }}")`): for a value read decoded (a style
 * attribute's), where no quote of the markup's is left. Markup read raw
 * keeps the rule above: a plain quote there may be the attribute's own,
 * which ends the value.
 */
function urlArgAt(s, u, stops, spaceBeforeParen, nonEmpty, otherQuote = false) {
    const n = s.length;
    const p = spaceBeforeParen ? skipSpace(s, u + 3) : u + 3;
    if (s[p] !== "(")
        return undefined;
    const w = skipSpace(s, p + 1);
    const q = s[w];
    if (q === "'" || q === '"') {
        // with `otherQuote`, the address runs to its own quote or a `)`: CSS
        // reads the other quote inside it as a character, so a style value read
        // decoded, `url("a.png?e={{ person['id'] }}")`, is one address (it was
        // read as none, and the picture lost unseen)
        const stop = (otherQuote ? stops.of(`${q})`) : stops).from(w + 1);
        if (stop < 0 || s[stop] !== q || (nonEmpty && stop === w + 1))
            return undefined;
        const k = skipSpace(s, stop + 1);
        return s[k] === ")" ? { arg: s.slice(w + 1, stop), end: k + 1, at: w + 1 } : undefined;
    }
    if (w >= n)
        return undefined;
    const stop = stops.from(w);
    if (stop < 0 || s[stop] !== ")")
        return undefined;
    if (nonEmpty && stop === w) {
        // `url( )`: the space before the paren gives its last character back
        // to the argument, which must hold one
        return w > p + 1 ? { arg: s[w - 1], end: w + 1, at: w - 1 } : undefined;
    }
    return { arg: s.slice(w, stop), end: stop + 1, at: w };
}
const URL_WORD = /url/gi;
/**
 * The matches of `/url\s*\(\s*(['"]?)([^'")]*)\1\s*\)/gi` (see urlArgAt for
 * the variants), in order, as their argument (the second group).
 */
function cssUrlArgs(s, spaceBeforeParen = true, nonEmpty = false) {
    return cssUrlMatches(s, spaceBeforeParen, nonEmpty).map((m) => m.arg);
}
exports.cssUrlArgs = cssUrlArgs;
/** The matches cssUrlArgs reads, with where each starts and ends. */
function cssUrlMatches(s, spaceBeforeParen = true, nonEmpty = false) {
    const out = [];
    const stops = new NextOf(s, `'")`);
    let from = 0;
    for (;;) {
        URL_WORD.lastIndex = from;
        const m = URL_WORD.exec(s);
        if (!m)
            break;
        const hit = urlArgAt(s, m.index, stops, spaceBeforeParen, nonEmpty);
        if (hit) {
            out.push({ start: m.index, end: hit.end, arg: hit.arg });
            from = hit.end;
        }
        else
            from = m.index + 1;
    }
    return out;
}
exports.cssUrlMatches = cssUrlMatches;
/** The argument of the `url(` at `u` as `url\(\s*(["']?)([^"')]*)\1\s*\)` reads it, and the index after it. */
function cssUrlAt(s, u, stops) {
    return urlArgAt(s, u, stops, false, false);
}
exports.cssUrlAt = cssUrlAt;
const QUOTE_OR_ENTITY = /['"]|&quot;|&#0*34;|&#x0*22;|&#0*39;|&#x0*27;|&apos;/iy;
/**
 * The quote at `i` in `s`: a quote character, or a quote written as an
 * entity (&quot;, &#34; and &#x22; with any number of zeros, &#39;, &#x27;,
 * &apos;, in either case); its length, 0 when none stands there. The one
 * reading of a quote for the import's url() reader (quotedUrlMatches, which
 * the picture finder reads by), its width hints and its rewrite of the
 * addresses it hosted: the rewrite looked at the 8 characters beside an
 * address, so `&#000034;` was found and hosted but never rewritten.
 */
function quoteLengthAt(s, i) {
    QUOTE_OR_ENTITY.lastIndex = i;
    const q = QUOTE_OR_ENTITY.exec(s);
    return q ? q[0].length : 0;
}
exports.quoteLengthAt = quoteLengthAt;
/**
 * The quote (quoteLengthAt) that ends right before `end` and starts at or
 * after `floor`: its length, 0 for none. An entity is read back from its
 * `;` over its name or number to its `&`, so each run of characters is
 * read back once from the `;` after it.
 */
/**
 * Which quote a quote reading (quoteLengthAt) stands for, however it is
 * spelled: '"' for `"`, &quot;, &#34; and &#x22; (any zeros, any case), "'"
 * for `'`, &#39;, &#x27; and &apos;. The browser decodes the entities of a
 * style attribute before CSS reads it, so `url(&quot;img/a.png&#34;)` is
 * `url("img/a.png")`: two spellings of one quote pair.
 */
function quoteKind(quote) {
    const q = quote.toLowerCase();
    if (q === '"' || q === "&quot;")
        return '"';
    if (q === "'" || q === "&apos;")
        return "'";
    // a numeric quote (quoteLengthAt's &#0*34;, &#x0*22;, &#0*39;, &#x0*27;): its zeros lead, so its
    // last two digits say which (read from its end: a pattern over the zeros retried from each of them)
    const digits = q.slice(-3, -1);
    return digits === "34" || digits === "22" ? '"' : "'";
}
exports.quoteKind = quoteKind;
function quoteLengthBefore(s, end, floor = 0) {
    if (end <= floor)
        return 0;
    const c = s[end - 1];
    if (c === '"' || c === "'")
        return 1;
    if (c !== ";")
        return 0;
    let k = end - 2;
    while (k >= floor && s[k] !== "&" && /[0-9A-Za-z#]/.test(s[k]))
        k -= 1;
    if (k < floor || s[k] !== "&")
        return 0;
    return quoteLengthAt(s, k) === end - k ? end - k : 0;
}
exports.quoteLengthBefore = quoteLengthBefore;
/**
 * An address as quotedUrlMatches reads one: without the quote written as an
 * entity round it (`&quot;img/hero.png&quot;`, or two spellings of the same
 * quote, `&quot;img/hero.png&#34;`, see quoteKind), and the blank space
 * inside it. What a reading that knows only plain quotes gives (html_scan's
 * paintedRefs) is read so, so both give one address for one picture.
 */
function withoutEntityQuotes(ref) {
    const L = quoteLengthAt(ref, 0);
    if (L < 2 || ref.length < L + 2)
        return ref;
    const R = quoteLengthBefore(ref, ref.length, L);
    if (!R || ref.length < L + R + 1)
        return ref;
    if (quoteKind(ref.slice(0, L)) !== quoteKind(ref.slice(ref.length - R)))
        return ref;
    return ref.slice(L, ref.length - R).trim();
}
exports.withoutEntityQuotes = withoutEntityQuotes;
/**
 * The matches of
 * `/url\(\s*(?:("|&quot;|&#0*34;|&#x0*22;)([^'")]+?)("|&quot;|&#0*34;|&#x0*22;)|('|&#0*39;|&#x0*27;|&apos;)([^'")]+?)('|&#0*39;|&#x0*27;|&apos;)|([^'")]+?))\s*\)/gi`
 * (the import's CSS_URL, `\1` read as any spelling of the quote it opened
 * with): where each starts and ends, its address, and where that starts
 * (`at`).
 *
 * After the space, a quote (or a quote written as an entity) opens the
 * address, which runs lazily to the same quote, then space and `)`; failing
 * that, the address runs from the space to the next quote or `)`, which
 * must be a `)`, leaving out the space before it. The address holds no
 * quote or `)`, so every `url(` in it ends where it does: the pattern read
 * that run again from each of them. "The same quote" is any spelling of it
 * (quoteKind): the browser reads `url(&quot;a.png&#34;)` and
 * `url("a.png&quot;)` in a style attribute as `url("a.png")`, and a picture
 * named so was refused as missing from its zip.
 */
function quotedUrlMatches(s) {
    const out = [];
    const stops = new NextOf(s, `'")`);
    const word = /url\(/gi;
    const spaceBefore = (r, floor) => {
        let z = r;
        while (z > floor && /\s/.test(s[z - 1]))
            z -= 1;
        return z;
    };
    let from = 0;
    for (;;) {
        word.lastIndex = from;
        const m = word.exec(s);
        if (!m)
            break;
        from = m.index + 1;
        const p = m.index + 4;
        const w = skipSpace(s, p);
        let hit;
        QUOTE_OR_ENTITY.lastIndex = w;
        const q = QUOTE_OR_ENTITY.exec(s);
        if (q) {
            const L = q[0].length;
            const kind = quoteKind(q[0]);
            // opened by an entity, the address runs to its own quote or a `)`: a
            // plain other quote is a character of it, to the attribute and to CSS
            // (`url(&quot;a.png?e={{ person['id'] }}&quot;)`); opened by a plain
            // quote, a plain quote of either kind ends it, as it may end the
            // attribute it stands in
            const r = (L === 1 ? stops : stops.of(`${kind})`)).from(w + L);
            if (L === 1 && r > w + 1 && s[r] === q[0]) {
                // a quote: the address ends at the next quote or `)`, which must be it
                const k = skipSpace(s, r + 1);
                if (s[k] === ")")
                    hit = { arg: s.slice(w + 1, r), end: k + 1, at: w + 1 };
            }
            else if (L > 1 && r > w + L && s[r] === kind) {
                // an entity, closed by the plain quote it spells
                const k = skipSpace(s, r + 1);
                if (s[k] === ")")
                    hit = { arg: s.slice(w + L, r), end: k + 1, at: w + L };
            }
            else if (r >= 0 && s[r] === ")") {
                // the same quote in any spelling (an entity: a plain one would have
                // stopped the run), then only space before the `)`
                const z = spaceBefore(r, w + L);
                const R = quoteLengthBefore(s, z, w + L + 1);
                if (R > 1 && quoteKind(s.slice(z - R, z)) === kind)
                    hit = { arg: s.slice(w + L, z - R), end: r + 1, at: w + L };
            }
        }
        if (!hit) {
            // no quote taken: the address runs from the space to a `)`
            const r = stops.from(w);
            if (r > w && s[r] === ")")
                hit = { arg: s.slice(w, Math.max(w + 1, spaceBefore(r, w))), end: r + 1, at: w };
            else if (r === w && s[r] === ")" && w > p)
                hit = { arg: s[w - 1], end: w + 1, at: w - 1 };
        }
        if (hit) {
            out.push({ start: m.index, end: hit.end, arg: hit.arg, at: hit.at });
            from = hit.end;
        }
    }
    return out;
}
exports.quotedUrlMatches = quotedUrlMatches;
const BACKGROUND_WORD = /background/gi;
const IMAGE_SUFFIX = /-image/iy;
/**
 * `s` with what each template tag holds ({{ ... }} or {% ... %}, each to its
 * first close) written as `_`, its braces kept: the same length, so an index
 * into it is one into `s`. The send fills a tag before any browser reads the
 * CSS round it, so a quote, a paren or a `;` inside one (`default: 'x'`,
 * `append: '(1)'`, `split: ';'`) ends no url() and no declaration. One pass:
 * each close is searched for once, and a tag never closed leaves the rest.
 */
function maskTemplateTags(s) {
    if (s.indexOf("{") < 0)
        return s;
    let out = "";
    let from = 0;
    let at = 0;
    for (;;) {
        const b = s.indexOf("{", at);
        if (b < 0 || b + 1 >= s.length)
            break;
        const kind = s[b + 1];
        if (kind !== "{" && kind !== "%") {
            at = b + 1;
            continue;
        }
        const close = s.indexOf(kind === "{" ? "}}" : "%}", b + 2);
        if (close < 0)
            break;
        out += s.slice(from, b + 2) + "_".repeat(close - b - 2);
        from = close;
        at = close + 2;
    }
    return from ? out + s.slice(from) : s;
}
exports.maskTemplateTags = maskTemplateTags;
/**
 * `/background(?:-image)?\s*:[^;]*?url\(\s*(['"]?)([^'")]+)\1\s*\)/i.exec(s)`,
 * as its second group, or undefined when it does not match.
 *
 * The first `background:` whose value, before its `;`, holds a `url(...)`
 * that reads, and the first such `url(...)` in it. Whether a `url(` reads
 * does not depend on the declaration it sits in, so each is read once.
 */
function backgroundUrlArg(source) {
    // (read with each template tag as one piece: see maskTemplateTags)
    const s = maskTemplateTags(source);
    const urls = [];
    const reads = [];
    URL_WORD.lastIndex = 0;
    for (let m = URL_WORD.exec(s); m; m = URL_WORD.exec(s)) {
        if (s[m.index + 3] === "(")
            urls.push(m.index);
        URL_WORD.lastIndex = m.index + 1;
    }
    if (!urls.length)
        return undefined;
    const stops = new NextOf(s, `'")`);
    const semis = new NextOf(s, ";");
    // the first url( at index >= k that reads, as an index into urls
    let k = 0;
    const readAt = (j) => {
        let r = reads[j];
        if (r === undefined) {
            // a style value read decoded: a quoted address may hold the other quote (see urlArgAt), read where it
            // stands in the value as written (its template tags whole, see maskTemplateTags)
            const hit = urlArgAt(s, urls[j], stops, false, true, true);
            reads[j] = r = hit ? source.slice(hit.at, hit.at + hit.arg.length) : null;
        }
        return r;
    };
    for (let from = 0;;) {
        BACKGROUND_WORD.lastIndex = from;
        const b = BACKGROUND_WORD.exec(s);
        if (!b)
            return undefined;
        from = b.index + 1;
        const e = at(IMAGE_SUFFIX, s, b.index + 10) ? b.index + 16 : b.index + 10;
        const c = skipSpace(s, e);
        if (s[c] !== ":")
            continue;
        const semi = semis.from(c + 1);
        const limit = semi < 0 ? s.length : semi;
        while (k < urls.length && urls[k] < c + 1)
            k += 1;
        let j = k;
        while (j < urls.length && urls[j] < limit && readAt(j) === null)
            j += 1;
        // the url( before j that did not read are passed for every later start too
        k = j;
        if (j < urls.length && urls[j] < limit)
            return readAt(j);
    }
}
exports.backgroundUrlArg = backgroundUrlArg;
// ---------------------------------------------------------------------------
// !important
const IMPORTANT_WORD = /important/iy;
const IMPORTANT_MARK = /!\s*important/gi;
const IMPORTANT_MARK_UNSPACED = /!important/gi;
const isSpace = (c) => /\s/.test(c);
/**
 * Where `/\s*!\s*important\s*$/i` matches in `v` (with `trailingSpace`
 * false, `/\s*!\s*important$/i`; with `spaced` false, `/\s*!important\s*$/i`),
 * or -1. The word ends where the value's
 * trailing space starts; before it, space, then the `!`, then the space
 * before that, which is where the match starts. The pattern tried every
 * start of a long run of space, reading it to its end each time (12 s for
 * a declaration of 128,000 spaces).
 */
function importantTailAt(v, trailingSpace = true, spaced = true) {
    let e = v.length;
    if (trailingSpace)
        while (e > 0 && isSpace(v[e - 1]))
            e -= 1;
    const k = e - 9;
    if (k < 1)
        return -1;
    IMPORTANT_WORD.lastIndex = k;
    if (!IMPORTANT_WORD.test(v))
        return -1;
    let q = k;
    if (spaced)
        while (q > 0 && isSpace(v[q - 1]))
            q -= 1;
    if (q === 0 || v[q - 1] !== "!")
        return -1;
    let a = q - 1;
    while (a > 0 && isSpace(v[a - 1]))
        a -= 1;
    return a;
}
exports.importantTailAt = importantTailAt;
/** `v.replace(/\s*!\s*important\s*$/i, "")` (see importantTailAt for the variants). */
function withoutImportantTail(v, trailingSpace = true, spaced = true) {
    const at = importantTailAt(v, trailingSpace, spaced);
    return at < 0 ? v : v.slice(0, at);
}
exports.withoutImportantTail = withoutImportantTail;
/**
 * `v.replace(/\s*!\s*important/gi, "")` (with `spaced` false,
 * `/\s*!important/gi`): each `!important`, and the run of space before it
 * back to the last one taken out, which is where the pattern's match starts
 * (see importantTailAt).
 */
function withoutImportant(v, spaced = true) {
    const mark = spaced ? IMPORTANT_MARK : IMPORTANT_MARK_UNSPACED;
    let out = "";
    let last = 0;
    mark.lastIndex = 0;
    for (let m = mark.exec(v); m; m = mark.exec(v)) {
        let a = m.index;
        while (a > last && isSpace(v[a - 1]))
            a -= 1;
        out += v.slice(last, a);
        last = m.index + m[0].length;
    }
    return last ? out + v.slice(last) : v;
}
exports.withoutImportant = withoutImportant;
// ---------------------------------------------------------------------------
// Readings the import engine takes of one kind of markup
/**
 * styleElementsOf's reading: `/<style\b[^>]*>[\s\S]*?<\/style>|<!--/gi`
 * taken a match at a time, in one pass. That pattern rescanned to the end of
 * the document from every `<style` that has no `</style>` after it: a tail
 * of them cost the square of the document's length (18 s at 128 KB). Once
 * one `<style` finds no `>`, or no `</style>` after its `>`, no later one
 * can, and only comments are left to skip.
 */
function readStyleElements(html) {
    const out = [];
    if (!/<style/i.test(html))
        return out;
    const open = /<style\b/gi;
    const close = /<\/style>/gi;
    // the next <style and the next comment at or after `from`, each kept while
    // it lies ahead (-1: none), so neither is searched for twice
    let nextStyle = -2;
    let nextComment = -2;
    let nextEnd = -2;
    let nextBang = -2;
    let from = 0;
    for (;;) {
        if (nextStyle !== -1 && nextStyle < from) {
            open.lastIndex = from;
            const o = open.exec(html);
            nextStyle = o ? o.index : -1;
        }
        if (nextComment !== -1 && nextComment < from)
            nextComment = html.indexOf("<!--", from);
        const start = nextStyle;
        const comment = nextComment;
        if (start < 0 && comment < 0)
            break;
        if (start >= 0 && (comment < 0 || start < comment)) {
            const gt = html.indexOf(">", start + 6);
            close.lastIndex = gt + 1;
            const c = gt < 0 ? null : close.exec(html);
            if (!c) {
                // this <style> ends nowhere, and neither does any later one
                nextStyle = -1;
                from = start + 1;
                continue;
            }
            out.push(html.slice(start, c.index + c[0].length));
            from = c.index + c[0].length;
            continue;
        }
        const at = comment + 4;
        // `<!-->` and `<!--->` close at once; otherwise the first `-->`
        // (or the `--!>` parsers also accept) ends the comment
        let end;
        if (html.startsWith(">", at))
            end = at + 1;
        else if (html.startsWith("->", at))
            end = at + 2;
        else {
            if (nextEnd !== -1 && nextEnd < at)
                nextEnd = html.indexOf("-->", at);
            if (nextBang !== -1 && nextBang < at)
                nextBang = html.indexOf("--!>", at);
            const a = nextEnd;
            const b = nextBang;
            if (a < 0 && b < 0)
                break;
            end = a < 0 ? b + 4 : b < 0 || a < b ? a + 3 : b + 4;
        }
        from = end;
    }
    return out;
}
exports.readStyleElements = readStyleElements;
/**
 * The matches of `/#(u_[a-z_0-9]+)([^{]*)\{([^}]*)\}/g` in `css`, as
 * [whole, id, the rest of the selector, body], found in one pass. The
 * pattern split a long id between its first two groups every way there is
 * before failing on a rule with no `{` or `}` after it. The id is the
 * longest run a split can take (the first one tried), and once one rule
 * finds no `{` or no `}` after it, no later one can.
 */
function unlayerIdRules(css) {
    const out = [];
    const idChar = /[a-z_0-9]/;
    let from = 0;
    for (;;) {
        const p = css.indexOf("#u_", from);
        if (p < 0)
            break;
        let e = p + 3;
        while (e < css.length && idChar.test(css[e]))
            e += 1;
        if (e === p + 3) {
            from = p + 1;
            continue;
        }
        const open = css.indexOf("{", e);
        const close = open < 0 ? -1 : css.indexOf("}", open + 1);
        if (close < 0)
            break;
        out.push([css.slice(p, close + 1), css.slice(p + 1, e), css.slice(e, open), css.slice(open + 1, close)]);
        from = close + 1;
    }
    return out;
}
exports.unlayerIdRules = unlayerIdRules;
/**
 * `/<[a-z][^>]*\sstyle\s*=\s*("[^"]*WORD[^"]*"|'[^']*WORD[^']*')/i.test(html)`
 * where `word` is a case-blind regex for WORD: whether a tag's style
 * attribute, before the tag's first `>`, opens a quoted value that holds
 * WORD (the value itself may run past that `>`). Tags that start inside one
 * tag share its `>`, so each run of the document is read once, and each
 * quoted value once.
 */
function someStyleNames(html, word) {
    const open = /<[a-z]/gi;
    const attr = /\sstyle\s*=\s*(["'])/gi;
    // the first style attribute at or after a place, kept while it lies ahead
    let next;
    const attrFrom = (i) => {
        if (next === undefined || (next && next.index < i)) {
            attr.lastIndex = i;
            next = attr.exec(html);
        }
        return next;
    };
    let from = 0;
    for (;;) {
        open.lastIndex = from;
        const o = open.exec(html);
        if (!o)
            return false;
        const gt = html.indexOf(">", o.index + 2);
        const end = gt < 0 ? html.length : gt;
        for (let a = attrFrom(o.index + 2); a && a.index < end; a = attrFrom(a.index + 1)) {
            const q = a[1];
            const valueStart = a.index + a[0].length;
            const close = html.indexOf(q, valueStart);
            if (close >= 0 && word.test(html.slice(valueStart, close)))
                return true;
        }
        if (gt < 0)
            return false;
        from = gt + 1;
    }
}
exports.someStyleNames = someStyleNames;
/**
 * `/[.#][\w-]+[^\s>+~]*$/.test(sel)`: after the selector's last space or
 * combinator, a `.` or `#` followed by a name character. `[\w-]` is a part
 * of `[^\s>+~]`, so the pattern split a long tail between the two every way
 * there is before failing; this reads it once.
 */
function endsInClassOrId(sel) {
    let from = 0;
    for (let i = sel.length - 1; i >= 0; i -= 1)
        if (/[\s>+~]/.test(sel[i])) {
            from = i + 1;
            break;
        }
    for (let p = from; p + 1 < sel.length; p += 1)
        if ((sel[p] === "." || sel[p] === "#") && /[\w-]/.test(sel[p + 1]))
            return true;
    return false;
}
exports.endsInClassOrId = endsInClassOrId;
/**
 * The matches of `/font-family\s*:\s*([^;"<>]*)(;|(?="))/gi`: where each
 * starts and ends and its first group. The value runs to the next `;`, `"`,
 * `<` or `>`; it matches when that is a `;` (taken) or a `"` (not). The
 * pattern split the space after the colon between `\s*` and the value every
 * way there is before failing at a `<`, and every declaration in one run
 * ends where the run does, so this reads each run once.
 */
function fontFamilyDecls(t) {
    const out = [];
    const word = /font-family\s*:/gi;
    const stops = new NextOf(t, ';"<>');
    let from = 0;
    for (;;) {
        word.lastIndex = from;
        const m = word.exec(t);
        if (!m)
            break;
        const w = skipSpace(t, m.index + m[0].length);
        const r = stops.from(w);
        if (r < 0)
            break;
        if (t[r] === ";" || t[r] === '"') {
            out.push({ start: m.index, end: t[r] === ";" ? r + 1 : r, value: t.slice(w, r) });
            from = t[r] === ";" ? r + 1 : r;
        }
        else
            from = r + 1;
    }
    return out;
}
exports.fontFamilyDecls = fontFamilyDecls;
/** `s.replace(/(<img\b[^>]*>)\s+/i, "$1")`: the space after the first <img> that has any, taken out. */
function spaceAfterFirstImgDropped(s) {
    const open = /<img\b/gi;
    for (let from = 0;;) {
        open.lastIndex = from;
        const o = open.exec(s);
        if (!o)
            return s;
        const gt = s.indexOf(">", o.index + 4);
        if (gt < 0)
            return s;
        const after = skipSpace(s, gt + 1);
        if (after > gt + 1)
            return s.slice(0, gt + 1) + s.slice(after);
        // every <img that starts inside this tag ends at the same `>`
        from = gt + 1;
    }
}
exports.spaceAfterFirstImgDropped = spaceAfterFirstImgDropped;
/** `s.replace(/\s+(<img\b)/i, "$1")`: the space before the first <img that has any, taken out. */
function spaceBeforeFirstImgDropped(s) {
    const open = /<img\b/gi;
    for (let m = open.exec(s); m; m = open.exec(s)) {
        if (m.index === 0 || !/\s/.test(s[m.index - 1]))
            continue;
        let a = m.index - 1;
        while (a > 0 && /\s/.test(s[a - 1]))
            a -= 1;
        return s.slice(0, a) + s.slice(m.index);
    }
    return s;
}
exports.spaceBeforeFirstImgDropped = spaceBeforeFirstImgDropped;
/**
 * The matches of TABLE_GAP (see keepRowLogicInCells), as [whole, tag, text] with their index, read in
 * one pass: the tag runs to its first `>`, the text to the next `<`, which
 * must open a table part. Every start inside one tag shares its `>` and its
 * text, so a start that fails sends the scan past that `>`, where the
 * pattern retried from each of them.
 */
function tableGaps(html) {
    const out = [];
    const open = /<\/?(?:table|tbody|thead|tfoot|tr)\b/gi;
    const part = /<\/?(?:table|tbody|thead|tfoot|tr)\b/iy;
    let from = 0;
    for (;;) {
        open.lastIndex = from;
        const o = open.exec(html);
        if (!o)
            break;
        const gt = html.indexOf(">", o.index + o[0].length);
        if (gt < 0)
            break;
        const lt = html.indexOf("<", gt + 1);
        part.lastIndex = lt;
        if (lt > gt + 1 && part.test(html)) {
            out.push({ index: o.index, 0: html.slice(o.index, lt), 1: html.slice(o.index, gt + 1), 2: html.slice(gt + 1, lt) });
            from = lt;
        }
        else
            from = gt + 1;
    }
    return out;
}
exports.tableGaps = tableGaps;
// ---------------------------------------------------------------------------
// Liquid inside an attribute
const isAsciiLetter = (c) => (c >= 65 && c <= 90) || (c >= 97 && c <= 122);
// the space an HTML tokenizer reads between a tag's parts
const isTagSpace = (c) => c === 32 || c === 9 || c === 10 || c === 12 || c === 13;
/** The most characters a Liquid tag in an attribute or an address is read across. */
const LIQUID_TAG_MAX = 2048;
const isLineEnd = (c) => c === 10 || c === 13 || c === 0x2028 || c === 0x2029;
// the words after which a Liquid string can follow
const LIQUID_BEFORE_STRING = new Set(["contains", "and", "or", "when", "if", "elsif", "unless", "in", "case", "echo", "render", "include", "layout", "section", "cycle", "with", "for", "as", "capture", "assign"]);
/**
 * Markup that only a STRAY opening reads across: a known element's opening or closing tag followed by `>`, `/>` or
 * an attribute (`"><a href=`, `</p>`, `<style>`). A comparison (`a<b and c>d`, `score<person.limit`) is not this.
 */
const MARKUP_AT = /^<\/?(?:a|abbr|b|big|blockquote|body|br|center|code|dd|div|dl|dt|em|font|form|h[1-6]|head|hr|html|i|img|input|label|li|link|meta|ol|p|pre|s|small|span|strike|strong|style|sub|sup|table|tbody|td|tfoot|th|thead|title|tr|u|ul|button|section|header|footer|caption|mark|nobr|del|ins|svg|article|nav|main|aside|figure|picture|source|video|v:[a-z]+|o:[a-z]+)(?:>|\/>|\s+[A-Za-z_:][-A-Za-z0-9_:.]*\s*=)/i;
/** An attribute's start inside a string (` href="`, ` class='`): a string of a stray opening ran past its attribute's end. */
const ATTRIBUTE_START = /\s[A-Za-z_:][-A-Za-z0-9_:.]*\s*=\s*(?:["']|$)/;
/**
 * Whether a quote at `at` in a Liquid tag (whose content starts at `from`)
 * can open a string: it follows an operator or a comma, a colon, a bracket
 * (`default: "x"`, `== "x"`, `["x"]`) or a keyword (`contains "x"`). Right
 * after the tag's opening, or after a name or a value (`{{ x"`), it cannot.
 */
function stringMayStartAt(s, from, at) {
    let p = at - 1;
    while (p >= from && isTagSpace(s.charCodeAt(p)))
        p--;
    // The tag's first token may be a string (`{{ "now" | date: "%Y" }}`, `{{- "pal" -}}`).
    if (p < from)
        return true;
    if (p === from && s.charCodeAt(p) === 45)
        return true;
    const c = s.charCodeAt(p);
    if (c === 34 || c === 39 || c === 41 || c === 93 || c === 125)
        return false;
    if (!(isAsciiLetter(c) || (c >= 48 && c <= 57) || c === 95))
        return true;
    let w = p;
    while (w > from && (isAsciiLetter(s.charCodeAt(w - 1)) || (s.charCodeAt(w - 1) >= 48 && s.charCodeAt(w - 1) <= 57) || s.charCodeAt(w - 1) === 95))
        w--;
    return LIQUID_BEFORE_STRING.has(s.slice(w, p + 1));
}
/**
 * Where the Liquid tags of `s` close, read as the send's liquidjs reads
 * them: the tag that opens at `i` (`{{` or `{%`) closes past its first `}}`
 * or `%}` outside its quoted strings, on its line. A string's quotes hold
 * anything but a line's end: `{{ x | default: "}}" }}` and `{{ x | default:
 * "{{y}}" }}` are one tag each, which the send renders. Outside its strings
 * a tag holds a `<`, a `>`, a lone `}` or an opening brace as its own
 * characters (`{% if person.score < 5 %}`, `default: "{"`).
 *
 * Once a tag opens it is read to its own close: a `<` is the tag's own
 * character (`{% if person.score<person.limit %}`, `default: "<b>"`), except
 * a known element's tag with `>`, `/>` or an attribute outside the tag's
 * strings (MARKUP_AT), or, in an attribute, a string that holds a `>` and then
 * another attribute's start (ATTRIBUTE_START): a stray opening ran across
 * markup there, and it is no tag.
 * It is no tag where it holds a line's end, another tag's opening outside
 * its strings, or a string that does not close: a stray `{{` in an address
 * (`?q={{"`) is read as no tag, and the markup stays markup.
 *
 * With `inAttribute`, the reading is for a tag in an attribute's value: a
 * `<` and a letter, `/` or `!` followed by a `>` anywhere in the tag, in a
 * string or out of it, is markup that a stray `{{` ran into (`href='?q={{'>our
 * guide</a>...`), and the tag is no tag. A comparison without spaces
 * (`person.score<person.limit`) holds no `>` and stays one tag.
 *
 * Returns -1 where it does not close. Each tag is read across at most
 * LIQUID_TAG_MAX characters, so a reading of the whole markup stays linear.
 */
function liquidTagEnds(s, attrQuote = 0) {
    return (i) => {
        const close = s.charCodeAt(i + 1) === 123 ? 125 : 37;
        const lim = Math.min(s.length, i + LIQUID_TAG_MAX);
        let j = i + 2;
        while (j < lim) {
            const c = s.charCodeAt(j);
            if (isLineEnd(c))
                return -1;
            if (c === 34 || c === 39) {
                // the attribute's own quote opens a string only where a string can start
                // (after an operator or a keyword); anywhere else it closes the attribute
                if (c === attrQuote && !stringMayStartAt(s, i + 2, j))
                    return -1;
                // a quoted string, to its closing quote
                let k = j + 1;
                while (k < lim && s.charCodeAt(k) !== c) {
                    if (isLineEnd(s.charCodeAt(k)))
                        return -1;
                    k++;
                }
                if (k >= lim)
                    return -1;
                // in an attribute, a string that holds another attribute's start ran past this attribute's end: a stray
                // (only past a `>` in the string: a stray that ran across markup; `default: "Your total = "` and
                // `default: "<a href='y'>z</a>"` are strings)
                if (attrQuote) {
                    const body = s.slice(j + 1, Math.min(k, j + 1 + LIQUID_TAG_MAX));
                    const gt = body.indexOf(">");
                    if (gt >= 0 && ATTRIBUTE_START.test(body.slice(gt)))
                        return -1;
                }
                j = k + 1;
                continue;
            }
            if (c === close && s.charCodeAt(j + 1) === 125)
                return j + 2;
            // outside its strings a tag holds no element markup: a stray opening ran across it
            if (c === 60 && MARKUP_AT.test(s.slice(j, j + 64)))
                return -1;
            if (c === 123 && (s.charCodeAt(j + 1) === 123 || s.charCodeAt(j + 1) === 37))
                return -1;
            j++;
        }
        return -1;
    };
}
exports.liquidTagEnds = liquidTagEnds;
/**
 * A Liquid tag (as liquidTagEnds reads it) with each string quoted in `q`
 * that holds a tag's close (`}}` or `%}`) quoted in the other quote, where it
 * holds none of that: `{{ x | default: "}}" }}` as `{{ x | default: '}}' }}`.
 * The send decodes character references in a tag only up to its first `}}`
 * or `%}` (LiquidEngine's `{{.*?}}`), and a double quote in an attribute is
 * written as `&quot;`: the closing one, past that `}}`, stayed `&quot;` to
 * liquidjs, the string did not close, and the send refused the email. The
 * string reads the same in either quote.
 */
function liquidStringsQuoted(tag, q) {
    if (tag.indexOf(q) < 0)
        return tag;
    const other = q === '"' ? "'" : '"';
    let out = "";
    let last = 0;
    for (let j = 2; j < tag.length; j++) {
        const c = tag[j];
        if (c !== '"' && c !== "'")
            continue;
        const k = tag.indexOf(c, j + 1);
        if (k < 0)
            break;
        const body = tag.slice(j + 1, k);
        if (c === q && (body.indexOf("}}") >= 0 || body.indexOf("%}") >= 0)) {
            // (a string holding both quotes keeps its quote and writes the close's second character as a
            // reference, which the send decodes in the tag with the rest: `it's }}` as `it's }&#125;`)
            out +=
                tag.slice(last, j) +
                    (body.indexOf(other) < 0 ? other + body + other : q + body.replace(/\}\}/g, "}&#125;").replace(/%\}/g, "%&#125;") + q);
            last = k + 1;
        }
        j = k;
    }
    return last ? out + tag.slice(last) : tag;
}
exports.liquidStringsQuoted = liquidStringsQuoted;
/**
 * The send's Liquid tags in `s`, as [start, end) pairs, read as the send
 * reads them (see liquidTagEnds): from the left, each `{{` or `{%` to its
 * close, and past a tag's end the next one. Linear in `s`.
 */
function liquidTagSpans(s) {
    const out = [];
    const end = liquidTagEnds(s);
    let i = s.indexOf("{");
    while (i >= 0 && i < s.length - 1) {
        const c = s.charCodeAt(i + 1);
        if (c === 123 || c === 37) {
            const e = end(i);
            if (e > 0) {
                out.push([i, e]);
                i = s.indexOf("{", e);
                continue;
            }
        }
        i = s.indexOf("{", i + 1);
    }
    return out;
}
exports.liquidTagSpans = liquidTagSpans;
/**
 * `html` with each quote inside a Liquid tag (`{{ ... }}`, `{% ... %}`) in a
 * quoted attribute value, of the kind that quotes the value, written as its
 * character reference: `href="https://shop.example/a?n={{ person.firstName |
 * default: "friend" }}&amp;e=..."` as `...default: &quot;friend&quot; }}...`.
 * The send renders the Liquid before any HTML reader sees the markup, so the
 * attribute is the whole address there; an HTML reader ends it at the first
 * quote inside the tag, and the import cut the card's links to
 * `https://shop.example/a?n=` while the grade, reading the source the same
 * way, said every link was kept. The send decodes character references in a
 * Liquid tag (LiquidEngine), so the tag renders as it was written, and the
 * editor's link fields show it with its quotes.
 *
 * Tags are read as an HTML tokenizer reads them (a `<` and a letter, the
 * name, then attributes up to the `>` outside a quoted value). Read
 * forward: a Liquid tag that does not close (see liquidTagEnds) is read
 * again as the value's characters, and no tag is read across more than
 * LIQUID_TAG_MAX characters. A string in the tag that holds a tag's close
 * takes the other quote (see liquidStringsQuoted).
 */
function liquidQuotesInAttributes(html) {
    if (html.indexOf("{{") < 0 && html.indexOf("{%") < 0)
        return html;
    const n = html.length;
    let out = "";
    let last = 0;
    let i = 0;
    const tagEnds = { '"': liquidTagEnds(html, 34), "'": liquidTagEnds(html, 39) };
    while (i < n) {
        const lt = html.indexOf("<", i);
        if (lt < 0)
            break;
        i = lt + 1;
        if (!isAsciiLetter(html.charCodeAt(i)))
            continue;
        // the tag's name
        while (i < n && !isTagSpace(html.charCodeAt(i)) && html[i] !== "/" && html[i] !== ">")
            i++;
        // its attributes
        while (i < n) {
            while (i < n && (isTagSpace(html.charCodeAt(i)) || html[i] === "/"))
                i++;
            if (i >= n || html[i] === ">")
                break;
            // the name (a first "=" is part of it)
            i++;
            while (i < n && !isTagSpace(html.charCodeAt(i)) && html[i] !== "/" && html[i] !== ">" && html[i] !== "=")
                i++;
            while (i < n && isTagSpace(html.charCodeAt(i)))
                i++;
            if (html[i] !== "=")
                continue;
            i++;
            while (i < n && isTagSpace(html.charCodeAt(i)))
                i++;
            const q = html[i];
            if (q !== '"' && q !== "'") {
                // an unquoted value ends at a space or the tag's end
                while (i < n && !isTagSpace(html.charCodeAt(i)) && html[i] !== ">")
                    i++;
                continue;
            }
            i++;
            while (i < n && html[i] !== q) {
                if (html[i] === "{" && (html[i + 1] === "{" || html[i + 1] === "%")) {
                    const end = tagEnds[q](i);
                    if (end > 0) {
                        // (the stored markup quotes its values in double quotes, so a string that holds a close
                        // takes single quotes whatever quote this attribute is written in)
                        const tag = liquidStringsQuoted(html.slice(i, end), '"');
                        if (tag !== html.slice(i, end) || tag.indexOf(q) >= 0) {
                            out += html.slice(last, i) + tag.split(q).join(q === '"' ? "&quot;" : "&#39;");
                            last = end;
                        }
                        i = end;
                        continue;
                    }
                }
                i++;
            }
            // (past the closing quote)
            i++;
        }
        // (past the tag's ">")
        i++;
    }
    return last ? out + html.slice(last) : html;
}
exports.liquidQuotesInAttributes = liquidQuotesInAttributes;
/**
 * The matches of
 * `/(<\/?(?:table|tbody|thead|tfoot|tr)\b[^>]*>)((?:[^<]|<!--(?:(?!-->)[\s\S])*-->)+)(?=<\/?(?:table|tbody|thead|tfoot|tr)\b)/gi`,
 * as [whole, tag, text] with their index: tableGaps, where a gap's text may
 * also hold HTML comments (`{% endfor %}<!-- end of items -->`), each ending
 * at its first `-->`. A comment never closed ends no gap.
 *
 * One pass. As in tableGaps, a start that fails sends the scan past its
 * tag's `>`. The walk through a run of comments is kept by where each comment
 * starts, so a later start whose walk reaches one of them (a table tag
 * written inside a comment) takes its end from there instead of walking the
 * run again, and the end of the last comment found (or that there is none
 * from some place on) is kept too: a run of comments each holding a `<tr>`
 * was read again from each of them, which is quadratic.
 */
function tableGapsThroughComments(html) {
    return tableGapsThrough(html, false);
}
exports.tableGapsThroughComments = tableGapsThroughComments;
/**
 * The markup a gap between table rows may hold (tableGapsThroughMarkup):
 * every element but a table's own parts and the three the browser reads
 * where they stand in a table (a script, a style, a template). Between rows
 * the browser draws all the others before the table, as it draws the gap's
 * words: words' own elements, a line break, a picture, a paragraph or a
 * heading, and a block too (a <div>, a <center>, a list, a quote, a button,
 * a form's words, a section, an <svg>, a <noscript>...). A gap whose blocks
 * do not close inside it is none (see gapBlocksClosed).
 */
const GAP_MARKUP = /<\/?(?!(?:table|tbody|thead|tfoot|tr|td|th|caption|colgroup|col|script|style|template)(?=[\s/>]))[A-Za-z][A-Za-z0-9:-]*(?=[\s/>])/iy;
/** The inline markup of GAP_MARKUP, which a gap may hold unclosed, as it always could. */
const GAP_INLINE = /^(?:a|abbr|b|bdi|bdo|big|br|cite|code|del|dfn|em|font|h[1-6]|hr|i|img|ins|kbd|mark|nobr|p|q|s|samp|small|span|strike|strong|sub|sup|tt|u|var|wbr)$/i;
/** Elements that never hold anything, so never close. */
const GAP_VOID = /^(?:area|base|br|col|embed|hr|img|input|keygen|link|meta|param|source|track|wbr)$/i;
/**
 * Elements whose end tag the markup may leave out (a list's items, a
 * definition list's terms and details, a select's options): the parser
 * closes one at the next of its kind and at the end of what holds it
 * (`<ul><li>One<li>Two</ul>`), and at a gap's end the next row closes it.
 */
const GAP_OPTIONAL_END = /^(?:li|dt|dd|option|optgroup|rb|rt|rtc|rp)$/i;
/**
 * Whether the blocks in a gap's text (GAP_MARKUP's but GAP_INLINE's and
 * the void ones) each close inside it, and close only what opened there: a
 * gap that ends at a table inside a <div> it opened (`{% if a %}<div><table>`)
 * is no gap, as before, or the <div> would go one way and its table another.
 * An element whose end tag may be left out (GAP_OPTIONAL_END) counts only
 * where the gap ends at a table (`atTable`), which it would hold: elsewhere
 * the parser closes it, as it closes a list's `<li>Two` at its `</ul>`.
 * One pass over the text.
 */
const GAP_TAG_NAME = /<(\/?)([A-Za-z][A-Za-z0-9:-]*)/y;
const gapBlocksClosed = (text, atTable = false) => {
    if (!/<[A-Za-z/]/.test(text))
        return true;
    const open = new Map();
    // each tag to its first `>` (a comment to its `-->`), and on from there
    for (let i = text.indexOf("<"); i >= 0;) {
        if (text.startsWith("<!--", i)) {
            const e = text.indexOf("-->", i + 4);
            if (e < 0)
                break;
            i = text.indexOf("<", e + 3);
            continue;
        }
        GAP_TAG_NAME.lastIndex = i;
        const m = GAP_TAG_NAME.exec(text);
        const gt = m ? text.indexOf(">", i + m[0].length) : -1;
        if (!m || gt < 0) {
            i = text.indexOf("<", i + 1);
            continue;
        }
        i = text.indexOf("<", gt + 1);
        const name = m[2].toLowerCase();
        if (GAP_INLINE.test(name) || GAP_VOID.test(name) || (!m[1] && text[gt - 1] === "/"))
            continue;
        if (!atTable && GAP_OPTIONAL_END.test(name))
            continue;
        const n = (open.get(name) || 0) + (m[1] ? -1 : 1);
        if (n < 0)
            return false;
        open.set(name, n);
    }
    for (const n of open.values())
        if (n)
            return false;
    return true;
};
/** A `<` that opens no tag: the browser reads it as a word (`{% if n < 5 %}`, `a <= b`). */
const NOT_A_TAG = /<(?![A-Za-z/!?])/y;
/**
 * tableGapsThroughComments, where a gap's text may also hold inline markup
 * (GAP_MARKUP: `{% if vip %}<strong>Members</strong> save more.`) and a `<`
 * that opens no tag, each tag running to its first `>`. The browser draws
 * all of it before the table, as it draws a gap's words; a gap that meets
 * any other tag before a table part (a cell, a div) is none, as before.
 * One pass, as tableGapsThroughComments.
 */
function tableGapsThroughMarkup(html, tick) {
    return tableGapsThrough(html, true, tick);
}
exports.tableGapsThroughMarkup = tableGapsThroughMarkup;
function tableGapsThrough(html, markup, tick) {
    const out = [];
    const open = /<\/?(?:table|tbody|thead|tfoot|tr)\b/gi;
    const part = /<\/?(?:table|tbody|thead|tfoot|tr)\b/iy;
    // the "-->" at or after a place: the last answer kept, and where none is left
    let askedAt = -1;
    let answer = -1;
    let noneFrom = Infinity;
    const closeFrom = (i) => {
        if (i >= noneFrom)
            return -1;
        if (askedAt <= i && i <= answer)
            return answer;
        const r = html.indexOf("-->", i);
        if (r < 0) {
            noneFrom = i;
            return -1;
        }
        askedAt = i;
        answer = r;
        return r;
    };
    // where the walk from each comment's start ends: the first "<" from there that opens no comment, or -1
    // (with `markup`, also none of GAP_MARKUP's tags, each to its first `>`, and no `<` that opens no tag)
    const walked = new Map();
    const passes = (p) => {
        if (html.startsWith("<!--", p))
            return true;
        if (!markup)
            return false;
        NOT_A_TAG.lastIndex = p;
        if (NOT_A_TAG.test(html))
            return true;
        GAP_MARKUP.lastIndex = p;
        return GAP_MARKUP.test(html);
    };
    const walk = (lt) => {
        const seen = [];
        let p = lt;
        while (p >= 0 && passes(p)) {
            const known = walked.get(p);
            if (known !== undefined) {
                p = known;
                break;
            }
            seen.push(p);
            if (html.startsWith("<!--", p)) {
                const end = closeFrom(p + 4);
                p = end < 0 ? -1 : html.indexOf("<", end + 3);
            }
            else if (html[p + 1] && /[A-Za-z/]/.test(html[p + 1])) {
                const gt = html.indexOf(">", p + 1);
                p = gt < 0 ? -1 : html.indexOf("<", gt + 1);
            }
            else
                p = html.indexOf("<", p + 1);
        }
        for (const s of seen)
            walked.set(s, p);
        return p;
    };
    let from = 0;
    for (let n = 0;; n += 1) {
        if (tick && !(n & 255))
            tick();
        open.lastIndex = from;
        const o = open.exec(html);
        if (!o)
            break;
        const gt = html.indexOf(">", o.index + o[0].length);
        if (gt < 0)
            break;
        let lt = html.indexOf("<", gt + 1);
        if (lt >= 0)
            lt = walk(lt);
        if (lt > gt + 1 && at(part, html, lt)) {
            // A gap whose blocks do not close inside it is none; what looked like a
            // row's tag inside it (in a comment, `<!--<tr>-->`, or an attribute)
            // starts no gap of its own: read from the gap's end, each gap's text is
            // read once, and the walk stays linear (read again from each such tag,
            // a gap of thousands of them took seconds).
            if (!markup || gapBlocksClosed(html.slice(gt + 1, lt), /^<table\b/i.test(html.slice(lt, lt + 7))))
                out.push({ index: o.index, 0: html.slice(o.index, lt), 1: html.slice(o.index, gt + 1), 2: html.slice(gt + 1, lt) });
            from = lt;
        }
        else
            from = gt + 1;
    }
    return out;
}
//# sourceMappingURL=html_scan.js.map